import { Component } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { CrmLeadSource } from "@enums/crmLeadSource.enum";
import { CrmOpportunityStage } from "@enums/crmOpportunityStage.enum";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { AdminHeaderButton } from "@interfaces/adminHeaderButton.interface";
import { CrmContact } from "@interfaces/models/crmContact.interface";
import { CrmLead } from "@interfaces/models/crmLead.interface";
import { CrmOpportunity } from "@interfaces/models/crmOpportunity.interface";
import { CrmTask } from "@interfaces/models/crmTask.interface";
import { Client } from "@interfaces/models/client.interface";
import { UserProfile } from "@interfaces/userProfile.interface";
import { AuthService } from "@services/auth.service";
import { ClientService } from "@services/client.service";
import { CrmContactService } from "@services/crmContact.service";
import { CrmLeadService } from "@services/crmLead.service";
import { CrmOpportunityService } from "@services/crmOpportunity.service";
import { CrmTaskService } from "@services/crmTask.service";
import { QuotationAnalysisFormService } from "@services/quotationAnalysisForm.service";
import { SalesEnquiryService } from "@services/salesEnquiry.service";
import { UserProfileService } from "@services/userProfile.service";
import { ToastService } from "angular-toastify";

@Component({
	moduleId: module.id,
	selector: "crm-opportunities-id",
	styleUrls: ["crmOpportunities-id.css"],
	templateUrl: "crmOpportunities-id.html"
})
export class CrmOpportunityIdAdminComponent {
	allSalesEnquiries: any[] = [];
	clients: Client[] = [];
	contacts: CrmContact[] = [];
	headerPrimaryButtons: AdminHeaderButton[] = [];
	item: CrmOpportunity = new CrmOpportunity();
	itemId = 0;
	itemType = "CRM Opportunity";
	leads: CrmLead[] = [];
	linkableQafs: any[] = [];
	linkedQafs: any[] = [];
	linkedSalesEnquiries: any[] = [];
	newTask: CrmTask = new CrmTask();
	readOnly = true;
	returnUrl: string = location.href.split("/")[3] + "/" + location.href.split("/")[4];
	sources: any[] = [];
	stages: any[] = [];
	tasks: CrmTask[] = [];
	title: string = "View " + this.itemType;
	userProfiles: UserProfile[] = [];

	constructor(private router: Router, private toastService: ToastService, private route: ActivatedRoute, private itemService: CrmOpportunityService, private clientService: ClientService, private crmTaskService: CrmTaskService, private crmContactService: CrmContactService, private crmLeadService: CrmLeadService, private salesEnquiryService: SalesEnquiryService, private quotationAnalysisFormService: QuotationAnalysisFormService, private userProfileService: UserProfileService, private authService: AuthService) {
		this.openQaf = this.openQaf.bind(this);
		this.unlinkQaf = this.unlinkQaf.bind(this);
		this.sources = GetPropertiesFromEnum(CrmLeadSource);
		this.stages = GetPropertiesFromEnum(CrmOpportunityStage);
		this.buildButtons("view");
		this.route.params.subscribe((params) => {
			if (params.id !== "create") {
				if (params.id.split("_").length > 1) {
					this.itemId = params.id.split("_")[0];
					this.getItem(this.itemId);
					this.edit();
				} else {
					this.itemId = params.id;
					this.getItem(params.id);
				}
			} else {
				this.create();
			}
		});
		this.getClients();
		this.getContacts();
		this.getLeads();
		this.getSalesEnquiries();
		this.getUserProfiles();
	}

	get filteredContacts() {
		if (!this.item.crmLeadId) return [];
		return this.contacts.filter(c => c.crmLeadId === this.item.crmLeadId);
	}

	addTask() {
		this.newTask.crmOpportunityId = this.item.id;
		this.crmTaskService.createSingle(this.newTask).subscribe(
			() => { this.toastService.success("Task added!"); this.newTask = new CrmTask(); this.getTasks(); },
			(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
		);
	}

	buildButtons(mode: string) {
		if (mode === "edit") {
			this.headerPrimaryButtons = [
				{ method: "updateItem", text: "Save Changes" },
				{ method: "cancelEditing", text: "View Mode" },
				{ method: "close", text: "Close" }
			];
		} else if (mode === "create") {
			this.headerPrimaryButtons = [
				{ method: "createItem", text: "Save New Opportunity" },
				{ method: "close", text: "Close" }
			];
		} else {
			this.headerPrimaryButtons = [
				{ method: "edit", text: "Edit" },
				{ method: "close", text: "Close" }
			];
		}
	}

	cancelEditing() {
		this.readOnly = true;
		this.buildButtons("view");
		this.title = "View " + this.itemType;
		this.getItem(this.itemId);
	}

	close() {
		this.router.navigate([this.returnUrl]);
	}

	create() {
		this.readOnly = false;
		this.title = "New " + this.itemType;
		this.buildButtons("create");
		this.item = new CrmOpportunity();
		this.item.stage = CrmOpportunityStage.AwaitingRfq;
		this.route.queryParams.subscribe((qp) => {
			if (qp.leadId) { this.item.crmLeadId = +qp.leadId; }
			// A lead that has become a customer passes its client through, so the opportunity starts with one.
			if (qp.clientId) { this.item.clientId = +qp.clientId; }
		});
	}

	createItem() {
		this.itemService.createSingle(this.item)
			.subscribe(
				(res: any) => {
					this.toastService.success("Opportunity created!");
					this.router.navigate([`${this.returnUrl}/${res.response.id}`]);
				},
				(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
			);
	}

	// Starts a new QAF on this opportunity: the QAF page picks the client and opportunity up from the seed.
	createQaf() {
		if (!this.item.clientId) {
			this.toastService.error("Set the client on this opportunity before creating a QAF - a QAF needs a client.");
			return;
		}
		sessionStorage.setItem("qafSeed", JSON.stringify({ clientId: this.item.clientId, crmOpportunityId: this.item.id }));
		this.router.navigate([`/${this.returnUrl.split("/")[0]}/quotationAnalysisForms/create`]);
	}

	deleteTask(task: CrmTask) {
		this.crmTaskService.deleteSingleById(task.id).subscribe(
			() => { this.toastService.success("Task removed."); this.getTasks(); },
			(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
		);
	}

	displayQaf(qaf: any) {
		return qaf ? `${qaf.id} - ${qaf.partNumber || ""}` : "";
	}

	edit() {
		this.readOnly = false;
		this.title = "Edit " + this.itemType;
		this.buildButtons("edit");
	}

	getAssignedName(userProfileId: number | undefined): string {
		if (!userProfileId) return "Unassigned";
		return this.userProfiles.find(u => u.id === userProfileId)?.fullName ?? "Unknown";
	}

	getClients() {
		this.clientService.getAll()
			.subscribe(
				(res: any) => { this.clients = res.response.items; },
				(err) => { console.log(err); }
			);
	}

	getContacts() {
		this.crmContactService.getAll()
			.subscribe(
				(res: any) => { this.contacts = (res.response.items || []).map((c: any) => ({ ...c, fullName: ((c.firstName || "") + " " + (c.lastName || "")).trim() })); },
				(err) => { console.log(err); }
			);
	}

	getItem(id: any) {
		this.itemService.getSingleById(id)
			.subscribe(
				(res: any) => { this.item = res.response; this.refreshLinked(); },
				(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
			);
	}

	getLeads() {
		this.crmLeadService.getAll()
			.subscribe(
				(res: any) => { this.leads = (res.response.items || []).filter((l: any) => l.isActive); },
				(err) => { console.log(err); }
			);
	}

	// QAFs on this opportunity, and the client's other top-level QAFs that could be linked to it.
	getQafs() {
		if (!this.item?.id) return;
		this.quotationAnalysisFormService.getByCrmOpportunityId(this.item.id)
			.subscribe(
				(res: any) => { this.linkedQafs = res.response || []; },
				(err) => { console.log(err); }
			);
		this.quotationAnalysisFormService.getWithNoParent()
			.subscribe(
				(res: any) => {
					this.linkableQafs = (res.response || []).filter((q: any) => this.item.clientId && q.clientId === this.item.clientId && q.crmOpportunityId !== this.item.id);
				},
				(err) => { console.log(err); }
			);
	}

	getSalesEnquiries() {
		this.salesEnquiryService.getAll()
			.subscribe(
				(res: any) => { this.allSalesEnquiries = res.response.items || []; },
				(err) => { console.log(err); }
			);
	}

	getTasks() {
		if (!this.item?.id) return;
		this.crmTaskService.getByOpportunityId(this.item.id).subscribe(
			(res: any) => { this.tasks = res.response || []; },
			(err) => { console.log(err); }
		);
	}

	getUserProfiles() {
		this.userProfileService.getAll()
			.subscribe(
				(res: any) => { this.userProfiles = res.response.items; },
				(err) => { console.log(err); }
			);
	}

	headerButtonClick(method: any) {
		// @ts-ignore
		if (this[method]) this[method]();
	}

	linkQaf(e: any) {
		if (!e.value) return;
		const qafId = e.value;
		e.component.option("value", null);
		this.quotationAnalysisFormService.setCrmOpportunityForQAF(qafId, this.item.id)
			.subscribe(
				() => { this.toastService.success("QAF linked"); this.getQafs(); },
				(err) => { console.log(err); this.toastService.error("Could not link the QAF."); }
			);
	}

	openQaf(e: any) {
		this.router.navigate([`/${this.returnUrl.split("/")[0]}/quotationAnalysisForms/${e.row.data.id}`]);
	}

	refreshLinked() {
		if (this.item?.id) {
			this.linkedSalesEnquiries = this.allSalesEnquiries.filter((se: any) => se.crmOpportunityId === this.item.id);
			this.getQafs();
			this.getTasks();
		}
	}

	toggleTaskComplete(task: CrmTask) {
		const updated = { ...task, completedDate: !task.isCompleted ? new Date() : null, isCompleted: !task.isCompleted };
		this.crmTaskService.updateSingleById(task.id, updated).subscribe(
			() => { this.getTasks(); },
			(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
		);
	}

	unlinkQaf(e: any) {
		if (confirm(`Unlink QAF ${e.row.data.id} from this opportunity? The QAF itself is not deleted.`) === false) return;
		this.quotationAnalysisFormService.removeCrmOpportunityFromQAF(e.row.data.id)
			.subscribe(
				() => { this.toastService.success("QAF unlinked"); this.getQafs(); },
				(err) => { console.log(err); this.toastService.error("Could not unlink the QAF."); }
			);
	}

	updateItem() {
		this.itemService.updateSingleById(this.item.id, this.item)
			.subscribe(
				() => { this.toastService.success("Opportunity updated!"); this.cancelEditing(); },
				(err) => { console.log(err); this.toastService.error("Something Went Wrong!"); }
			);
	}
}
